import { model as i18nModel } from '!/i18n'
import { register } from '!/repository'
import { model as router } from '!/router'
import { aye, nay } from '@/helpers'
import { api } from '@setplex/tria-api'
import { combine, createEvent, restore, sample } from 'effector'
import { model as catchupsModel } from '~/entities/catchups'
import '~/features/payment/generic/model/select-catchup'
import { siginInToWatchFeatureModel } from '~/features/signin-to-watch'
import { model as guestModel } from '~/features/subscriber/guest'
import * as pinLockFeature from '~/features/pin-lock'
import type {
  CatchupFormatted,
  CatchupProgramFormatted,
} from '~/shared/interfaces'
import '~/shared/ui/translations/general-translations'

export const initCatchupPage = createEvent<{
  channelId?: string
  catchupId?: string
  timestamp?: string
}>()
export const initCatchup = createEvent<{
  channelId?: string
  catchupId?: string
  timestamp?: string
}>()

const $initParams = restore(initCatchup, {})

export const leftCatchupPage = createEvent<void>()
export const left = createEvent<void>()

export const loaded = createEvent<{
  channelId?: string
  catchupId?: string
  timestamp?: string
}>()

sample({
  clock: initCatchupPage,
  target: initCatchup,
})

// need for the case if the user returned to the blocked channel using the browser's back arrow
sample({
  clock: initCatchup,
  source: {
    allCatchups: catchupsModel.$allCatchups,
    prev: router.$previous,
  },
  filter: ({ prev }) => !prev?.pathname.match('catchup'),
  fn: ({ allCatchups }) => allCatchups,
  target: catchupsModel.setAllCatchupsToDisplay,
})

// load all catchups if there are zero loaded
sample({
  clock: initCatchup,
  source: { catchups: catchupsModel.$allCatchups },
  filter: ({ catchups }) => nay(catchups) || catchups.length === 0,
  target: catchupsModel.getAllCatchupsFx,
})

// in case all catchups are loaded - trigger `loaded` event
sample({
  clock: [initCatchup, catchupsModel.$allCatchups.updates],
  source: { catchups: catchupsModel.$allCatchups, payload: $initParams },
  filter: ({ catchups }) => aye(catchups) && catchups.length > 0,
  fn: ({ payload }) => payload,
  target: loaded,
})

// all catchups loaded - check params

// if there are no params -> redirect to the first loaded catchup
// if there are some params -> check if they are valid,
//   and if params are not valid -> redirect to the first loaded catchup
sample({
  clock: loaded,
  source: { catchups: catchupsModel.$allCatchups },
  filter: ({ catchups }, { catchupId, channelId }) =>
    (nay(catchupId) && nay(channelId)) ||
    catchups.findIndex(
      (c) => c.id === Number(catchupId) && c.channel.id === Number(channelId)
    ) === -1,
  fn: ({ catchups }) => {
    const catchup = catchups[0]
    const catchupId = catchup.id
    const channelId = catchup.channel.id
    return `/modules/catchups/${catchupId}/channel/${channelId}`
  },
  target: router.navigateReplace,
})

// if there is `catchupId` but no `channelId` -> find catchup by id and redirect to it
sample({
  clock: loaded,
  source: { catchups: catchupsModel.$allCatchups },
  filter: ({ catchups }, { catchupId, channelId }) =>
    aye(catchupId) &&
    nay(channelId) &&
    catchups.findIndex((c) => c.id === Number(catchupId)) >= 0,
  fn: ({ catchups }, { catchupId }) => {
    const catchup = catchups.find((c) => c.id === Number(catchupId))!
    const channelId = catchup.channel.id
    return `/modules/catchups/${catchupId}/channel/${channelId}`
  },
  target: router.navigateReplace,
})

// if params are valid -> set active catchup
sample({
  clock: loaded,
  source: { catchups: catchupsModel.$allCatchupsToDisplayFormatted },
  filter: ({ catchups }, { catchupId, channelId }) =>
    aye(catchupId) &&
    aye(channelId) &&
    catchups.findIndex(
      (c) => c.id === Number(catchupId) && c.channel.id === Number(channelId)
    ) >= 0,
  fn: ({ catchups }, { catchupId, channelId }) =>
    catchups.find(
      (c) => c.id === Number(catchupId) && c.channel.id === Number(channelId)
    )!,
  target: catchupsModel.setActiveCatchup,
})

// after dates for active catchup are loaded -> select first one, or set in query string
sample({
  clock: [loaded, catchupsModel.$activeChannelCatchupsPrograms],
  source: {
    dates: catchupsModel.$activeChannelCatchupsDatelist,
    payload: $initParams,
  },
  filter: ({ dates }) => aye(dates) && dates.length > 0,
  fn: ({ dates, payload }) => {
    const date = dates[0].stamp
    if (payload.timestamp) {
      const timestamp = Number(payload.timestamp)
      const found = dates.find(({ stamp }) => stamp === timestamp)
      if (found) {
        return found.stamp
      }
    }
    return date
  },
  target: catchupsModel.setActiveDate,
})

const $descriptionPinLockPopupTranslated = combine(i18nModel.$t, ({ t }) =>
  t('locked_popup_hint')
)

/********** Skeleton logics starts ***********/
export const setCatchupsContentLoading = createEvent<boolean>()
export const $catchupsContentLoading = restore(setCatchupsContentLoading, false)

export const setProgramsContentLoading = createEvent<boolean>()
export const $programsContentLoading = restore(setProgramsContentLoading, false)

const $isAnyProgramDataFxPending =
  catchupsModel.getChannelCatchupProgramsFx.pending

sample({
  clock: $isAnyProgramDataFxPending,
  source: { pathname: router.$pathname },
  filter: ({ pathname }) => Boolean(pathname?.startsWith('/catchups')),
  fn: (_, clockData) => clockData,
  target: setProgramsContentLoading,
})

export const $isAnyInitCatchupFxPending = catchupsModel.getAllCatchupsFx.pending

sample({
  clock: $isAnyInitCatchupFxPending,
  source: { pathname: router.$pathname },
  filter: ({ pathname }) => Boolean(pathname?.startsWith('/catchups')),
  fn: (_, clockData) => clockData,
  target: setCatchupsContentLoading,
})
/********** Skeleton logics ends ***********/

sample({
  clock: catchupsModel.callCatchupsPinLockPopup,
  fn: (callCatchupsPinLockPopup) => ({
    ...pinLockFeature.emptyPinLockState,
    ...callCatchupsPinLockPopup,
  }),
  target: pinLockFeature.openPinLockPopup,
})

sample({
  clock: leftCatchupPage,
  source: catchupsModel.$allCatchups,
  target: catchupsModel.setAllCatchupsToDisplay,
})

const $isNoContentBlock = combine(
  catchupsModel.$activeCatchup,
  catchupsModel.$activeChannelCatchupsDatelist,
  (activeCatchup, activeChannelCatchupsDatelist) =>
    !activeCatchup?.channel.locked && !activeChannelCatchupsDatelist.length
)

const selectCathchupProgram = createEvent<{
  catchup: CatchupFormatted
  program: CatchupProgramFormatted
}>()

sample({
  clock: selectCathchupProgram,
  source: {
    guest: guestModel.$guest,
    redirect: router.$href,
  },
  filter: ({ guest }, { catchup }) =>
    aye(guest) &&
    nay(catchup.channel.free) &&
    nay(catchup.channel.purchaseInfo),
  fn: ({ redirect }, { catchup }) => ({
    name: catchup.channel.name,
    redirect: redirect ?? '',
  }),
  target: siginInToWatchFeatureModel.openSignInToWatchPopup,
})

sample({
  clock: selectCathchupProgram,
  source: guestModel.$guest,
  filter: (guest, { catchup }) =>
    aye(guest) &&
    (aye(catchup.channel.free) || aye(catchup.channel.purchaseInfo)),
  fn: (_, catchupProgram) => catchupProgram,
  target: catchupsModel.selectCathchupProgram,
})

sample({
  clock: selectCathchupProgram,
  source: guestModel.$guest,
  filter: nay,
  fn: (_, catchupProgram) => catchupProgram,
  target: catchupsModel.selectCathchupProgram,
})

// router.refresh need to refresh stores after sucssesfull payment
catchupsModel.$activeCatchup.reset([router.refresh, api.session.signOutFx])
catchupsModel.$redirectToUnlockedCatchup.reset([
  router.refresh,
  api.session.signOutFx,
  initCatchup,
  left,
])
api.catchups.$selectedChannelDateList.reset([
  api.session.signOutFx,
  api.session.signInFx,
])
api.catchups.$channelsWithCatchups.reset([
  router.refresh,
  api.session.signOutFx,
  api.session.signInFx,
])

// reset catchups when user leaves catchup page, but not to player
sample({
  clock: left,
  source: router.$pathname,
  filter: (pathname) => !pathname?.startsWith('/player/catchup'),
  target: [
    catchupsModel.$activeCatchup.reinit,
    catchupsModel.$allCatchupsToDisplay.reinit,
  ],
})

register(catchupsModel.$allCatchupsToDisplayFormatted, '$allCatchupsToDisplay')
register(catchupsModel.$activeCatchup, '$activeCatchup')
register(catchupsModel.$activeChannelCatchupsDatelist, '$activeCatchupDateList')
register(catchupsModel.$activeDate, '$activeDate')

register(
  catchupsModel.$activeChannelCatchupsDatelist,
  '$activeChannelCatchupsDateList'
)

register(
  catchupsModel.$catchupsProgramsForSelectedDate,
  '$catchupsProgramsForSelectedDate'
)

register(selectCathchupProgram, 'selectCatchupProgram')
register(
  catchupsModel.setActiveUnlockedCatchups,
  'catchups/setActiveUnlockedCatchups'
)
register(catchupsModel.$seletedCatchupIndex, '$seletedCatchupIndex')

register(catchupsModel.$isCatchupsEmpty, '$isCatchupsEmpty')
register(
  $descriptionPinLockPopupTranslated,
  '$descriptionPinLockPopupTranslated'
)
register($isNoContentBlock, '$isNoContentBlock')
